import { HttpClient } from '@angular/common/http';
import {
	Component,
	computed,
	EventEmitter,
	HostListener,
	Input,
	input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	signal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '@environment/environment';
import { TranslocoService } from '@jsverse/transloco';
import { ViewportService } from '@services/geo/viewport.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { ValidationService } from '@services/ui/validation.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { InputComponent } from '@shared/components/input/input.component';
import { PasswordComponent } from '@shared/components/password/password.component';
import { SharedModule } from '@shared/modules/shared.module';
import { ForgotPasswordModalComponent } from './forgot-password-modal/forgot-password-modal.component';

interface DevLoginUser {
	label: string;
	email: string;
	role?: string;
}

/**
 * Una de las cajas del selector que arranca las pantallas de acceso
 * (`/access/candidates`, `/access/companies`): «Inicia sesión» y «Alta».
 *
 * Vive en el formulario de acceso y no en un componente aparte porque tiene que
 * ocupar EXACTAMENTE el hueco del formulario, dentro de la misma carcasa de dos
 * paneles. Un componente hermano habría tenido que repetir el panel derecho de
 * marca, y a la segunda vez que alguien tocara la marca los dos dejarían de
 * parecerse.
 */
export interface AuthChoice {
	/** Lo que se emite al pulsar. El contenedor decide qué hacer con ello. */
	key: string;
	/** Nombre de icono de lucide. */
	icon: string;
	titleKey: string;
	descriptionKey: string;
	ctaKey: string;
}

@Component({
	selector: 'app-auth-form',
	imports: [
		SharedModule,
		ButtonComponent,
		InputComponent,
		PasswordComponent,
		CheckboxComponent,
		ForgotPasswordModalComponent,
	],
	templateUrl: './auth-form.component.html',
	styleUrl: './auth-form.component.scss',
})
export class AuthFormComponent implements OnChanges, OnInit {
	// 1 = Login, 2 = Reset Password, 3 = Reset Password Confirm, 4 = MFA Verification,
	// 5 = Activate Account (same two fields as 3, different moment: first entry, no
	//     «volver al login» — the person has no password to go back to),
	// 6 = Activation link expired: ask for a fresh one (email only, like 2).
	@Input() isRegister!: 1 | 2 | 3 | 4 | 5 | 6;
	/** Prefills the email field (the expired-link screen already knows the address). */
	@Input() initialEmail: string | null = null;
	/**
	 * Activation only: the account has to accept the privacy policy before it
	 * chooses a password — a candidate migrated from the previous software
	 * (10/09). The backend is what enforces it; this draws the box.
	 */
	@Input() requirePrivacy = false;
	privacyAccepted = false;
	privacyTouched = false;
	@Output() formSubmit = new EventEmitter<{
		email?: string;
		password?: string;
		newPassword?: string;
		confirmPassword?: string;
		mfaCode?: string;
		trustDevice?: boolean;
		devLogin?: boolean;
		privacyAccepted?: boolean;
	}>();
	@Output() cancelMFA = new EventEmitter<void>();
	@Input() errorMessage: string | null = null;
	@Input() showSuccessState = false; // For reset-password-confirm success state
	@Input() loading = false; // Loading state for submit button

	/**
	 * Cuando viene relleno, el panel izquierdo pinta las cajas en vez del
	 * formulario. `null` = la pantalla de siempre, que es lo que ven `/login`,
	 * `/reset-password` y `/reset-password-confirm` sin tocar nada.
	 */
	@Input() choices: AuthChoice[] | null = null;
	@Input() choicesTitleKey = '';
	@Input() choicesSubtitleKey = '';
	@Output() choiceSelect = new EventEmitter<string>();

	/**
	 * Etiqueta del enlace «volver» bajo el formulario. Solo la ponen las
	 * pantallas de acceso: sin él, quien pulsa «Inicia sesión» se queda dentro
	 * del formulario sin forma de volver a las dos cajas salvo el botón atrás del
	 * navegador, que en una pantalla que no cambia de URL no hace lo que parece.
	 */
	@Input() backLabelKey: string | null = null;
	@Output() backClick = new EventEmitter<void>();

	// UI State
	showForgotPasswordModal = false;
	/** Legal notice, opened from the small link under the password field. */

	/** «Ojito» (A-P3-03): qué campos de contraseña se están enseñando en claro. */
	private readonly shownFields = signal<Record<string, boolean>>({});

	shown(field: string): boolean {
		return this.shownFields()[field] === true;
	}

	toggleShown(field: string): void {
		this.shownFields.update((current) => ({ ...current, [field]: !current[field] }));
	}
	inputSize: 'large' | 'medium' | 'small' = 'large'; // Default to large

	// MFA State
	mfaCode: string[] = ['', '', '', '', '', ''];
	trustDevice = false;

	// Dev login (localhost only)
	devLoginMenuOpen = false;
	devLoginUsers: DevLoginUser[] = [];
	devLoginAvailable = false;

	// Form
	authForm: FormGroup;

	// Navigation
	anchorUrl = input('');
	close = signal('/login');

	// Branding
	logo = computed(() => 'assets/layout/images/branding/km-monogram.png');

	constructor(
		private fb: FormBuilder,
		private validationService: ValidationService,
		private viewportService: ViewportService,
		private router: Router,
		private translocoService: TranslocoService,
		private http: HttpClient,
		private messageAlert: MessageAlertService,
	) {
		this.authForm = this.fb.group({});
	}

	/**
	 * Returns the form fields based on the form type (login/reset)
	 */
	get formFields(): string[] {
		switch (this.isRegister) {
			case 1: // Login
				return ['email', 'password'];
			case 2: // Reset Password
			case 6: // Activation link expired
				return ['email'];
			case 3: // Reset Password Confirm
			case 5: // Activate Account
				return ['newPassword', 'confirmPassword'];
			case 4: // MFA Verification
				return []; // No traditional form fields for MFA
			default:
				return [];
		}
	}

	/**
	 * Checks if MFA code is complete (all 6 digits filled)
	 */
	get isMfaCodeComplete(): boolean {
		return this.mfaCode.every((digit) => digit.length === 1);
	}

	ngOnInit(): void {
		this.updateFormValidations();
		this.checkViewportSize();
		this.loadDevLoginUsers();

		// Only add event listener in browser environment
		if (typeof window !== 'undefined') {
			// (resize handled by @HostListener below — the anonymous listener leaked per navigation)
		}
	}

	/**
	 * Whether to render the dev login. Hard-gated to local/Tailscale hosts on
	 * the login screen — never renders on a deployed host. Always visible in dev,
	 * even if the user list hasn't loaded (the menu retries on open).
	 */
	get showDevLogin(): boolean {
		return this.isRegister === 1 && this.isLocalhost();
	}

	toggleDevLoginMenu(): void {
		this.devLoginMenuOpen = !this.devLoginMenuOpen;
		if (this.devLoginMenuOpen && !this.devLoginAvailable) {
			this.loadDevLoginUsers();
		}
	}

	onDevLogin(user: DevLoginUser): void {
		if (this.loading) return;
		this.devLoginMenuOpen = false;
		this.formSubmit.emit({ email: user.email, devLogin: true });
	}

	private loadDevLoginUsers(): void {
		if (!this.isLocalhost()) return;

		// Pull the real users of the current backend schema. Emails are
		// plus-addressed per dev schema, so a static list can't work.
		this.http.get<DevLoginUser[]>(`${environment.pathBackend}/auth/dev-users`).subscribe({
			next: (users) => this.setDevLoginUsers(users),
			error: () => this.setDevLoginUsers([]),
		});
	}

	private setDevLoginUsers(users: DevLoginUser[]): void {
		this.devLoginUsers = (users || []).filter((user) => user.email);
		this.devLoginAvailable = this.devLoginUsers.length > 0;
	}

	/**
	 * Mirrors the backend gate (auth.service.ts `isDevLoginAllowed`): localhost
	 * plus the Tailscale CGNAT range (100.64.0.0/10), so a dev VPS reached over
	 * Tailscale gets the picker too. Any other host stays hidden, and the
	 * backend rejects it regardless.
	 */
	private isLocalhost(): boolean {
		if (typeof window === 'undefined') return false;
		const host = window.location.hostname;
		if (['localhost', '127.0.0.1', '::1'].includes(host)) return true;
		return /^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\.\d{1,3}\.\d{1,3}$/.test(host);
	}

	ngOnChanges(changes: { isRegister?: SimpleChanges; initialEmail?: SimpleChanges }): void {
		if (changes.isRegister || changes.initialEmail) {
			this.updateFormValidations();
		}
	}

	/**
	 * Checks viewport size and updates input size accordingly.
	 * Three-tier scaling: small for short laptops/tablets, medium for narrow desktops,
	 * large by default. Matches the height-aware SCSS breakpoints below.
	 */
	@HostListener('window:resize')
	onWindowResize(): void {
		this.checkViewportSize();
	}

	private checkViewportSize(): void {
		// Check if running in browser environment
		if (typeof window !== 'undefined') {
			const width = window.innerWidth;
			const height = window.innerHeight;

			if (width <= 1024 && height <= 700) {
				this.inputSize = 'small';
			} else if (width <= 1024) {
				this.inputSize = 'medium';
			} else {
				this.inputSize = 'large';
			}
		} else {
			// Default size for SSR
			this.inputSize = 'large';
		}
	}

	/**
	 * Updates form validations based on form type
	 */
	private updateFormValidations(): void {
		switch (this.isRegister) {
			case 1: // Login
				this.authForm = this.fb.group({
					email: ['', [Validators.required, this.validationService.emailValidator()]],
					password: ['', [Validators.required]],
				});
				break;

			case 2: // Reset Password
			case 6: // Activation link expired
				this.authForm = this.fb.group({
					email: [this.initialEmail ?? '', [Validators.required, this.validationService.emailValidator()]],
				});
				break;

			case 3: // Reset Password Confirm
			case 5: // Activate Account
				this.authForm = this.fb.group({
					newPassword: ['', [Validators.required, this.validationService.passwordValidator()]],
					confirmPassword: [
						'',
						[Validators.required, this.validationService.confirmPasswordValidator('newPassword')],
					],
				});
				break;

			default:
				this.authForm = this.fb.group({}); // Empty by default
		}
	}

	/**
	 * Handles form submission
	 */
	onSubmit(): void {
		if (this.loading) {
			return;
		}

		// Handle MFA submission separately
		if (this.isRegister === 4) {
			this.onMfaSubmit();
			return;
		}

		if (this.authForm.invalid) {
			this.authForm.markAllAsTouched();
			return;
		}
		if (this.isRegister === 5 && this.requirePrivacy && !this.privacyAccepted) {
			this.privacyTouched = true;
			return;
		}

		let formData: any = {};

		switch (this.isRegister) {
			case 1: // Login
			case 2: // Reset Password
			case 6: // Activation link expired
				formData = {
					email: this.authForm.get('email')?.value,
					password: this.authForm.get('password')?.value,
				};
				break;
			case 3: // Reset Password Confirm
			case 5: // Activate Account
				formData = {
					newPassword: this.authForm.get('newPassword')?.value,
					confirmPassword: this.authForm.get('confirmPassword')?.value,
					...(this.isRegister === 5 && this.requirePrivacy ? { privacyAccepted: this.privacyAccepted } : {}),
				};
				break;
		}

		this.formSubmit.emit(formData);
	}

	/**
	 * Handles MFA form submission
	 */
	onMfaSubmit(): void {
		if (!this.isMfaCodeComplete || this.loading) {
			return;
		}

		const mfaCode = this.mfaCode.join('');

		this.formSubmit.emit({
			mfaCode,
			trustDevice: this.trustDevice,
		});
	}

	/**
	 * Handles MFA cancellation
	 */
	onCancelMFA(): void {
		this.mfaCode = ['', '', '', '', '', ''];
		this.trustDevice = false;
		this.cancelMFA.emit();
	}

	/**
	 * Handles focus on MFA code boxes - selects the content
	 */
	onMfaFocus(event: Event): void {
		const input = event.target as HTMLInputElement;
		input.select();
	}

	/**
	 * Handles input in MFA code boxes
	 */
	onMfaInput(event: Event, index: number): void {
		const input = event.target as HTMLInputElement;
		const value = input.value;

		// Only allow numbers (remove any non-digit characters)
		const numericValue = value.replace(/\D/g, '');

		// Only allow single digit
		if (numericValue.length > 1) {
			input.value = numericValue.charAt(numericValue.length - 1);
		} else {
			input.value = numericValue;
		}

		this.mfaCode[index] = input.value;

		// Auto-focus next input and select its content
		if (input.value && index < 5) {
			const nextInput = input.parentElement?.nextElementSibling?.querySelector('input') as HTMLInputElement;
			if (nextInput) {
				nextInput.focus();
				nextInput.select(); // Select the content so it can be overwritten
			}
		}

		// NO auto-submit, user must click the button
	}

	/**
	 * Handles keydown in MFA code boxes (backspace navigation)
	 */
	onMfaKeydown(event: KeyboardEvent, index: number): void {
		const input = event.target as HTMLInputElement;

		// Handle Enter key to submit
		if (event.key === 'Enter') {
			event.preventDefault();
			const fullMfaCode = this.mfaCode.join('');
			if (fullMfaCode.length === 6) {
				this.onSubmit(); // Call the main submit which handles MFA
			}
			return;
		}

		if (event.key === 'Backspace' && !input.value && index > 0) {
			const prevInput = input.parentElement?.previousElementSibling?.querySelector('input');
			if (prevInput) {
				(prevInput as HTMLInputElement).focus();
			}
		}

		// Handle paste
		if (event.key === 'v' && (event.ctrlKey || event.metaKey)) {
			event.preventDefault();
			navigator.clipboard.readText().then((text) => {
				const digits = text.replace(/\D/g, '').slice(0, 6).split('');
				digits.forEach((digit, i) => {
					if (i < 6) {
						this.mfaCode[i] = digit;
						const inputElement = document.querySelector(`#mfa-${i}`) as HTMLInputElement;
						if (inputElement) {
							inputElement.value = digit;
						}
					}
				});
				// Focus last filled input and select it
				const lastIndex = Math.min(digits.length - 1, 5);
				const lastInput = document.querySelector(`#mfa-${lastIndex}`) as HTMLInputElement;
				if (lastInput) {
					lastInput.focus();
					lastInput.select();
				}
				// NO auto-submit after paste
			});
		}
	}

	/**
	 * Returns error message for a form field
	 */
	getErrorMessage(fieldName: string): string | null {
		const control = this.authForm.get(fieldName);
		if (!control?.errors) return null;

		// Handle specific errors for reset-password-confirm
		if (control.errors['required']) {
			return this.translocoService.translate('auth.errors.required');
		}
		if (control.errors['invalidPassword']) {
			return this.validationService.getValidatorErrorMessage('invalidPassword');
		}
		if (control.errors['passwordsDoNotMatch']) {
			return this.translocoService.translate('auth.errors.passwordsDoNotMatch');
		}

		// Default error handling
		const errorKey = Object.keys(control.errors)[0];
		return errorKey ? this.validationService.getValidatorErrorMessage(errorKey) : null;
	}

	/**
	 * Handles "Enter" key press for form submission
	 */
	submitOnEnter(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.onSubmit();
		}
	}

	submitOnEnterSafe(event: Event): void {
		this.submitOnEnter(event as KeyboardEvent);
	}

	/**
	 * Handles forgot password click based on viewport
	 */
	onForgotPasswordClick(): void {
		if (this.viewportService.isMobileView()) {
			this.showForgotPasswordModal = true;
		} else {
			this.router.navigate(['/reset-password']);
		}
	}

	/**
	 * Recepción del email en el modal de "olvidé la contraseña" (móvil; en
	 * escritorio el enlace navega a `/reset-password`, que hace esto mismo).
	 *
	 * El mensaje es **el mismo si el email existe y si no**: un formulario de
	 * login que distinga entre "te lo hemos enviado" y "ese usuario no existe"
	 * sirve para averiguar quién tiene cuenta.
	 */
	onForgotPasswordSubmit(email: string): void {
		this.showForgotPasswordModal = false;
		if (!email) {
			return;
		}
		const done = () =>
			this.messageAlert.showMessage(
				'success',
				this.translocoService.translate('auth.resetPassword.messages.emailSent'),
				this.translocoService.translate('auth.resetPassword.messages.emailSentDescription'),
			);
		this.http.post(`${environment.pathBackend}/auth/reset-password`, { email }).subscribe({
			next: done,
			error: done,
		});
	}

	/**
	 * Navigates back to login page
	 */
	navigateToLogin(): void {
		this.router.navigate(['/login']);
	}

	/** Which success card: the password reset's, or the "new link sent" of the expired activation. */
	successKey(part: 'title' | 'message' | 'button'): string {
		return this.isRegister === 6
			? `auth.activateAccount.resend.success.${part}`
			: `auth.resetPasswordConfirm.success.${part}`;
	}
}
